import { createContext, memo, ReactNode, useContext } from 'react';

import { Conversation } from '@web/conversations/types/Conversation';
import { Message } from '@web/conversations/types/Message';

import { useConversationContext } from './hooks/useConversationContext';
import { ConversationActions, ConversationState } from './conversationProvider.types';

type Props = {
  children: ReactNode;
  conversation: Conversation;
  messagesRefetchCounter: number;
  messages: Message[];
  selectedMessageId: ID | null;
};

export const ConversationStateContext = createContext<ConversationState>({} as ConversationState);
ConversationStateContext.displayName = 'ConversationStateContext';
export const useConversationState = () => useContext(ConversationStateContext);

export const ConversationActionContext = createContext<ConversationActions>(
  {} as ConversationActions
);
ConversationActionContext.displayName = 'ConversationActionContext';
export const useConversationActions = () => useContext(ConversationActionContext);

export const ConversationProvider = memo(
  ({ children, ...data }: Props) => {
    const { actions, state } = useConversationContext(data);

    return (
      <ConversationActionContext.Provider value={actions}>
        <ConversationStateContext.Provider value={state}>
          {children}
        </ConversationStateContext.Provider>
      </ConversationActionContext.Provider>
    );
  },
  // @INFO conversation object changes every time its react-query cache is updated
  // however we do not want to reinitialize conversation every time
  (prevProps, nextProps) => {
    const hasRefetchedMessages =
      prevProps.messagesRefetchCounter !== nextProps.messagesRefetchCounter;
    const hasConversationChanged = prevProps.conversation.id !== nextProps.conversation.id;
    const hasContextChanged = prevProps.selectedMessageId !== nextProps.selectedMessageId;

    const shouldReinitializeConversation =
      hasRefetchedMessages || hasConversationChanged || hasContextChanged;

    return !shouldReinitializeConversation;
  }
);

ConversationProvider.displayName = 'ConversationProvider';
